import { useCallback, useEffect, useState } from 'react';

export const useLazyLoadImages = () => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  const lazyload = useCallback(() => {
    if (!container) {
      return;
    }

    const images = container.querySelectorAll<HTMLImageElement>('[data-src]');
    const containerRect = container.getBoundingClientRect();

    images.forEach((img) => {
      const src = img.getAttribute('data-src');
      if (!src) {
        return;
      }

      const imgRect = img.getBoundingClientRect();

      if (
        imgRect.top <= containerRect.top + containerRect.height &&
        imgRect.bottom >= containerRect.top - containerRect.height &&
        imgRect.left <= containerRect.left + containerRect.width &&
        imgRect.right >= containerRect.left
      ) {
        img.src = src;
        img.style.opacity = '1';
        img.removeAttribute('data-src');
      }
    });
  }, [container]);

  const debouncedLazyload = useCallback(() => {
    let debounceTimer: NodeJS.Timeout;
    return () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(lazyload, 20);
    };
  }, [lazyload]);

  useEffect(() => {
    const debouncedHandler = debouncedLazyload();

    if (container) {
      container.addEventListener('scroll', debouncedHandler);
      window.addEventListener('resize', debouncedHandler);
      window.addEventListener('orientationchange', debouncedHandler);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', debouncedHandler);
        window.removeEventListener('resize', debouncedHandler);
        window.removeEventListener('orientationchange', debouncedHandler);
      }
    };
  }, [container, debouncedLazyload]);

  return { setContainer, lazyload };
};
